import { get, ref, set } from "firebase/database";
import {
  ActivityDomains,
  appendActivity,
  type ActivityActor,
  type ActivityAppendInput,
} from "./activityService";
import { db } from "./firebase";

const VERSION_CONTROL_PATH = "app_config/version_control";

export interface VersionControlConfig {
  minimum_required_build: number;
  recommended_build: number;
  force_update: boolean;
  update_message: string;
  reminder_interval_days: number;
  /** Show the global system status banner in the mobile app. */
  status_banner_enabled: boolean;
  /** Custom banner copy; empty uses the app default slow-backend message. */
  status_banner_message: string;
  /** Banner background as `#RRGGBB` (mobile parses this). */
  status_banner_color: string;
}

export const DEFAULT_STATUS_BANNER_COLOR = "#B45309";

export const DEFAULT_VERSION_CONTROL: VersionControlConfig = {
  minimum_required_build: 0,
  recommended_build: 0,
  force_update: false,
  update_message: "",
  reminder_interval_days: 7,
  status_banner_enabled: false,
  status_banner_message: "",
  status_banner_color: DEFAULT_STATUS_BANNER_COLOR,
};

function toNonNegInt(value: unknown, fallback: number): number {
  const n = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.max(0, Math.floor(n));
}

function toPositiveInt(value: unknown, fallback: number): number {
  const n = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.max(1, Math.floor(n));
}

/** Normalize to `#RRGGBB` uppercase, or [fallback] when invalid. */
export function normalizeHexColor(
  value: unknown,
  fallback: string = DEFAULT_STATUS_BANNER_COLOR,
): string {
  if (typeof value !== "string") return fallback;
  let s = value.trim();
  if (!s.startsWith("#")) s = `#${s}`;
  if (/^#[0-9A-Fa-f]{6}$/.test(s)) return s.toUpperCase();
  if (/^#[0-9A-Fa-f]{3}$/.test(s)) {
    const r = s[1];
    const g = s[2];
    const b = s[3];
    return `#${r}${r}${g}${g}${b}${b}`.toUpperCase();
  }
  return fallback;
}

function normalizeFromRtdb(raw: unknown): VersionControlConfig {
  if (!raw || typeof raw !== "object") {
    return { ...DEFAULT_VERSION_CONTROL };
  }
  const o = raw as Record<string, unknown>;
  return {
    minimum_required_build: toNonNegInt(
      o.minimum_required_build,
      DEFAULT_VERSION_CONTROL.minimum_required_build,
    ),
    recommended_build: toNonNegInt(
      o.recommended_build,
      DEFAULT_VERSION_CONTROL.recommended_build,
    ),
    force_update: Boolean(o.force_update),
    update_message:
      typeof o.update_message === "string"
        ? o.update_message
        : String(o.update_message ?? ""),
    reminder_interval_days: toPositiveInt(
      o.reminder_interval_days,
      DEFAULT_VERSION_CONTROL.reminder_interval_days,
    ),
    status_banner_enabled: Boolean(o.status_banner_enabled),
    status_banner_message:
      typeof o.status_banner_message === "string"
        ? o.status_banner_message
        : String(o.status_banner_message ?? ""),
    status_banner_color: normalizeHexColor(o.status_banner_color),
  };
}

export function normalizeVersionControlInput(
  data: VersionControlConfig,
): VersionControlConfig {
  return normalizeFromRtdb(data);
}

async function logActivitySafe(entry: ActivityAppendInput): Promise<void> {
  try {
    await appendActivity(entry);
  } catch (e) {
    console.error("Failed to record activity:", e);
  }
}

export async function getVersionControl(): Promise<VersionControlConfig> {
  const r = ref(db, VERSION_CONTROL_PATH);
  const snapshot = await get(r);
  if (!snapshot.exists()) {
    return { ...DEFAULT_VERSION_CONTROL };
  }
  return normalizeFromRtdb(snapshot.val());
}

export function validateVersionControl(data: VersionControlConfig): string[] {
  const errors: string[] = [];
  if (data.recommended_build < data.minimum_required_build) {
    errors.push(
      "Recommended build must be greater than or equal to minimum required build.",
    );
  }
  return errors;
}

const VERSION_CONTROL_DETAIL_KEYS: (keyof VersionControlConfig)[] = [
  "minimum_required_build",
  "recommended_build",
  "force_update",
  "update_message",
  "reminder_interval_days",
  "status_banner_enabled",
  "status_banner_message",
  "status_banner_color",
];

const VERSION_CONTROL_FIELD_LABELS: Record<keyof VersionControlConfig, string> =
  {
    minimum_required_build: "minimum required build",
    recommended_build: "recommended build",
    force_update: "force update",
    update_message: "update message",
    reminder_interval_days: "reminder interval",
    status_banner_enabled: "status banner enabled",
    status_banner_message: "status banner message",
    status_banner_color: "status banner color",
  };

function versionControlChangedFields(
  before: VersionControlConfig,
  after: VersionControlConfig,
): string[] {
  return VERSION_CONTROL_DETAIL_KEYS.filter((k) => before[k] !== after[k]);
}

function versionControlChangeSummary(changedFields: string[]): string {
  if (changedFields.length === 0) return "App release policy saved";
  const labels = changedFields.map(
    (k) => VERSION_CONTROL_FIELD_LABELS[k as keyof VersionControlConfig] ?? k,
  );
  return `App release policy updated (${labels.join(", ")})`;
}

export async function saveVersionControl(
  data: VersionControlConfig,
  actor?: ActivityActor | null,
): Promise<void> {
  const normalized = normalizeVersionControlInput(data);
  const errors = validateVersionControl(normalized);
  if (errors.length > 0) {
    throw new Error(errors.join(" "));
  }

  // Capture before-state only when we will audit-log (needs actor).
  const beforeSnapshot = actor ? await getVersionControl() : null;
  const r = ref(db, VERSION_CONTROL_PATH);
  await set(r, {
    minimum_required_build: normalized.minimum_required_build,
    recommended_build: normalized.recommended_build,
    force_update: normalized.force_update,
    update_message: normalized.update_message,
    reminder_interval_days: normalized.reminder_interval_days,
    status_banner_enabled: normalized.status_banner_enabled,
    status_banner_message: normalized.status_banner_message,
    status_banner_color: normalized.status_banner_color,
  });

  // Audit log only after RTDB write succeeds (activity-tracking.mdc)
  if (!actor || beforeSnapshot === null) return;

  const afterSnapshot = await getVersionControl();
  const changedFields = versionControlChangedFields(
    beforeSnapshot,
    afterSnapshot,
  );

  await logActivitySafe({
    domain: ActivityDomains.VersionControl,
    action: "update",
    resourceType: "version_control",
    resourceId: "app_config/version_control",
    summary: versionControlChangeSummary(changedFields),
    actor,
    details: {
      before: beforeSnapshot,
      after: afterSnapshot,
      changedFields,
    },
  });
}
